// MatterGen results: every job's extxyz in one view (editable-plan spec 0.7).
// Pure helpers: no React, no Firestore; the fetch function is passed in.
//
// A variant's candidates arrive as several jobs, each with its own extxyz file and its own
// per-crystal CIF and trajectory URLs. The results view used to keep only the first job's
// extxyz, so most candidates were never shown or captured. These helpers fetch every job,
// keep only complete frames, concatenate them in job order and re-index the per-frame file
// lists, so merged frame i (the index parseExtxyzFrames in AppShell.jsx assigns in parse
// order) maps to cifs[i] and trajectories[i] of its own job.

/**
 * Split extxyz text into complete frame blocks, using the same header walk as
 * parseExtxyzFrames: a line that parses as a positive atom count n starts a frame of
 * n + 2 lines, any other line is skipped, and a truncated last frame is dropped.
 * @param {string} text
 * @returns {string[]} one block per frame, without a trailing newline
 */
export function splitExtxyzFrames(text) {
  if (typeof text !== 'string' || text === '') return [];
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const n = parseInt(lines[i], 10);
    if (!Number.isFinite(n) || n <= 0) { i += 1; continue; }
    const end = i + 2 + n;
    if (end > lines.length) break;
    blocks.push(lines.slice(i, end).join('\n'));
    i = end;
  }
  return blocks;
}

/**
 * Concatenate every job's complete frames in job order and re-index the file lists.
 * A job without text (its fetch failed) is reported in `failed` and contributes nothing,
 * so later jobs' frames keep their own CIF and trajectory URLs.
 * @param {Array<{ text: string|null, cifs?: string[], trajectories?: string[], runId?: string|null, url?: string, error?: string }>} jobs
 * @returns {{ text: string, frameCount: number, cifs: Array<string|null>, trajectories: Array<string|null>, jobFrameCounts: number[], failed: Array<{ runId: string|null, url: string|null, error: string }> }}
 */
export function mergeExtxyzJobs(jobs) {
  const blocks = [];
  const cifs = [];
  const trajectories = [];
  const jobFrameCounts = [];
  const failed = [];
  for (const job of Array.isArray(jobs) ? jobs : []) {
    if (!job || typeof job.text !== 'string') {
      failed.push({ runId: job?.runId ?? null, url: job?.url ?? null, error: job?.error || 'No extxyz text' });
      jobFrameCounts.push(0);
      continue;
    }
    const jobBlocks = splitExtxyzFrames(job.text);
    const jobCifs = Array.isArray(job.cifs) ? job.cifs : [];
    const jobTrajs = Array.isArray(job.trajectories) ? job.trajectories : [];
    jobBlocks.forEach((block, k) => {
      blocks.push(block);
      cifs.push(jobCifs[k] || null);
      trajectories.push(jobTrajs[k] || null);
    });
    jobFrameCounts.push(jobBlocks.length);
  }
  return {
    text: blocks.length > 0 ? `${blocks.join('\n')}\n` : '',
    frameCount: blocks.length,
    cifs,
    trajectories,
    jobFrameCounts,
    failed,
  };
}

/**
 * Fetch every job's extxyz (each with up to `attempts` tries, `delayMs` apart, as the
 * file modal's single-file fetch does) and merge them with mergeExtxyzJobs.
 * @param {Array<{ url: string, runId?: string|null, cifs?: string[], trajectories?: string[] }>} jobs
 * @param {(url: string) => Promise<{ ok: boolean, status?: number, text: () => Promise<string> }>} fetchFn
 */
export async function loadExtxyzJobs(jobs, fetchFn, { attempts = 3, delayMs = 500 } = {}) {
  const list = Array.isArray(jobs) ? jobs : [];
  const loaded = await Promise.all(list.map(async (job) => {
    let error = 'Failed to fetch';
    for (let a = 0; a < attempts; a += 1) {
      try {
        const res = await fetchFn(job.url);
        if (res && res.ok) return { ...job, text: await res.text(), error: null };
        error = `HTTP ${res ? res.status : 'error'}`;
      } catch (e) {
        error = (e && e.message) || String(e);
      }
      if (a < attempts - 1) await new Promise((r) => setTimeout(r, delayMs));
    }
    return { ...job, text: null, error };
  }));
  return mergeExtxyzJobs(loaded);
}
